<template>
  <div class="fts-phone-number">
    <fts-select
      class="country-dropdown custom"
      :formData="phone"
      :item="{fieldName: 'code'}"
      :options="countries"
      :clearable="false"
      reduceKey="code"
      popperWidth="250px"
      @input="selected">
      <div slot="selected-option-container" slot-scope="{option}" class="fts-flag" :class="`flag-${option.id}`"></div>
      <font-awesome-icon slot="open-indicator" slot-scope="{attributes}" v-bind="attributes" :icon="['fas', 'angle-down']" class="select-open-indicator" />
      <div slot="option" slot-scope="{option}" class="country-option">
        <div class="fts-flag country-flag" :class="`flag-${option.id}`"></div>
        <div class="country-name">{{option.name}}</div>
        <div class="country-dial-code">{{option.dialCode}}</div>
      </div>
    </fts-select>
    <div class="phone-number__dial-code">{{phone.dialCode}}</div>
    <cleave
      class="fts-form-control phone-number__input"
      v-model="model"
      :options="options"
      autocomplete="ftsPhoneNumberInput"
      @blur="onBlur"
      ref="ftsPhoneNumberInput" />
  </div>
</template>

<script>
import Vue from 'vue';
import countriesList from "../../json/countries.json";
import { clientStore } from "../store/clientStore";

import parsePhoneNumber from 'libphonenumber-js'
import Cleave from "vue-cleave-component";
import "cleave.js/dist/addons/cleave-phone.i18n.js";
Vue.use(Cleave);

import FTSSelect from "./FTSSelect";

export default {
  components: {
    "fts-select": FTSSelect
  },
  props: {
    item: Object,
    formData: Object,
    preferredCountries: {
      type: Array,
      default: () => ['my', 'sg', 'us', 'gb']
    },
    excludeCountries: Array
  },
  data: () => ({
    phone: {},
    countriesList: countriesList
  }),
  methods: {
    selected(e) {
      const res = this.countries.find(x => x.code === e);
      if (res && res.dialCode) {
        this.$set(this.phone, 'dialCode', res.dialCode);
        this.$set(this.phone, 'code', res.code);
        // set default
        clientStore.dispatch("setPhoneNumberData", this.phone);
      }
      if (this.model) {
        this.onBlur();
      }
      // focus cleave input
      this.$nextTick(() => {
        this.$refs.ftsPhoneNumberInput.$el.focus();
      })
    },
    onBlur() {
      this.$emit('blur');
    },
  },
  computed: {
    countries() {
      let countries = _.cloneDeep(countriesList)
      // exclude
      if (Array.isArray(this.excludeCountries))
        countries = countries.filter(x => {
          return x.code && !this.excludeCountries.includes(x.id)
        })
      // re-arrange
      if (Array.isArray(this.preferredCountries))
        this.preferredCountries.reverse().forEach(x => {
          let country = countries.find(y => y.id === x)
          if (country && country.id) {
            countries.splice(countries.indexOf(country), 1)
            countries.unshift(country)
          }
        })
      return countries
    },
    model: {
      get() {
        const phoneNumber = this.formData[this.item.fieldName];
        try {
          if (phoneNumber) {
            const res = parsePhoneNumber(phoneNumber);
            if (res) {
              /* set dial code */
              if (res.countryCallingCode)
                this.$set(this.phone, 'dialCode', `+${res.countryCallingCode}`);
              /* set code (country short name) */
              if (res.country) 
                this.$set(this.phone, 'code', res.country);
              /* phone number (without dial code) */
              return res.nationalNumber;
            } else {
              const firstChar = phoneNumber.replaceAll(' ','').charAt(0);
              const nationalNumber = phoneNumber.replaceAll(' ','').replaceAll('+', '');
              if (firstChar === '+' && this.phone.dialCode) {
                return (`+${nationalNumber}`).replaceAll(this.phone.dialCode, '');
              }
              return nationalNumber;
            }
          }
        } catch(err) {
          console.error(err);
        }
      },
      set(val) {
        if (val)
          this.$set(this.formData, this.item.fieldName, this.phone.dialCode + val);
        else if (this.formData[this.item.fieldName])
          this.$delete(this.formData, this.item.fieldName);
      }
    },
    options() {
      return {
        phone: true, 
        phoneRegionCode: this.phone.code,
      }
    },
  },
  watch: {
    model(val, oldVal) {
      if (val !== oldVal) {
        this.$emit('input', val);
      }
    },
  },
  mounted() {
    this.phone = _.cloneDeep(clientStore.state.phoneNumberData);
  },
}
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

.fts-phone-number {
  position: relative;
  $country-dropdown-width: 55px;
  $dial-code-width: 40px;
  .phone-number__input {
    padding-left: calc(#{$country-dropdown-width + $dial-code-width});
  }
  .country-dropdown {
    position: absolute;
    left: 0;
    height: 100%;
    max-width: $country-dropdown-width;
    min-width: $country-dropdown-width;
    &.vs--open {
      .selected-option {
        opacity: .4;
      }
    }
    &.vs--searching {
      .selected-option {
        display: none;
      }
    }
    .vs__dropdown-toggle {
      height: 100%;
      padding: 0 0 0 .75rem;
      border: 0;
      font-size: .75rem;
      cursor: pointer;
    }
    .vs__selected-options {
      .selected-option {
        @include absolute-center-y;
      }
      .vs__search {
        /* old class override */
        min-width: 0 !important;
        margin: 0;
        padding: 0;
      }
    }
    .vs__actions {
      padding: 0;
      .select-open-indicator {
        color: var(--fts-gray-5);
      }
    }
  }
  .phone-number__dial-code {
    position: absolute;
    left: $country-dropdown-width;
    height: 100%;
    width: $dial-code-width;
    display: inline-flex;
    align-items: center;
    justify-content: end;
    font-size: .75rem;
    color: var(--fts-form-control-color);
  }
}
/* comfort form design */
.comfort-form-design {
  .fts-phone-number {
    $country-dropdown-width: 55px;
    $dial-code-width: 50px;
    .phone-number__input {
      padding-left: calc(#{$country-dropdown-width + $dial-code-width});
    }
    .country-dropdown {
      max-width: $country-dropdown-width;
      min-width: $country-dropdown-width;
    }
    .phone-number__dial-code {
      width: $dial-code-width;
      font-size: $comfortDesignFontSize;
    }
  }
}
/* country option */
.country-option {
  min-width: 0;
  display: flex;
  align-items: center;
  .country-flag {
    flex-shrink: 0;
  }
  .country-name {
    @include text-overflow-ellipsis;
    margin-left: .575rem;
    margin-right: .575rem;
  }
  .country-dial-code {
    font-size: .675rem;
    color: var(--fts-gray-5);
  }
}
</style>